import { MuscleVolumeAnalysis, Routine, RoutinesResponse, WeeklySplit } from '../types'
import { exerciseProcessor } from './exerciseProcessor'

export class RoutineService {
  /**
   * Fetch all routines from Hevy API with pagination
   */
  static async fetchAllRoutines(apiKey: string): Promise<Routine[]> {
    const MAX_PAGES = 5
    const fetchPromises = Array.from(
      { length: MAX_PAGES },
      (_, i) =>
        fetch(`https://api.hevyapp.com/v1/routines?page=${i + 1}`, {
          headers: { 'api-key': apiKey }
        })
          .then((res) => {
            if (!res.ok) return null
            return res.json()
          })
          .catch(() => null) // Silently catch all errors including 404s
    )

    const results = await Promise.all(fetchPromises)
    return results.filter(Boolean).flatMap((res: RoutinesResponse) => res.routines)
  }

  /**
   * Calculate muscle volume analysis from routines and weekly split
   */
  static calculateMuscleVolume(
    routines: Routine[],
    weeklySplit: WeeklySplit,
    includeSecondaryMovers: boolean
  ): MuscleVolumeAnalysis[] {
    const volume: Record<string, MuscleVolumeAnalysis> = {}
    const cycleMode = weeklySplit.cycle_mode || false
    
    // Calculate cycle frequency for normalization
    let cycleFrequency = 1
    if (cycleMode) {
      const activeDays = Object.entries(weeklySplit)
        .filter(([key, day]) => key !== 'cycle_mode' && Array.isArray(day) && day.length > 0)
        .length
      if (activeDays > 0) {
        cycleFrequency = 7 / activeDays
      }
    }

    Object.entries(weeklySplit).forEach(([day, dayRoutines]) => {
      if (day === 'cycle_mode' || !dayRoutines || dayRoutines.length === 0) return

      dayRoutines.forEach((routineId: string) => {
        if (!routineId || routineId === 'rest') return

        const routine = routines.find((r) => r.id === routineId)
        if (!routine) return

        routine.exercises.forEach((exercise) => {
        const contributions = exerciseProcessor.getMuscleContributionsFromExercise(
          exercise.title,
          exercise.exercise_template_id,
          includeSecondaryMovers
        )

        contributions.forEach(({ muscleGroup, isPrimary, weight }) => {
          if (!volume[muscleGroup]) {
            volume[muscleGroup] = {
              muscleGroup,
              weeklySetCount: 0,
              dailyBreakdown: {},
              exercises: []
            }
          }

          // Count all sets except explicitly empty ones (for routine planning)
          const sets = exercise.sets.filter((s) => s.type !== 'empty').length
          const weightedSets = sets * weight
          volume[muscleGroup].weeklySetCount += weightedSets
          volume[muscleGroup].dailyBreakdown[day] =
            (volume[muscleGroup].dailyBreakdown[day] || 0) + weightedSets

          const exEntry = volume[muscleGroup].exercises.find((e) => e.name === exercise.title)
          if (exEntry) {
            exEntry.sets += sets
            if (!exEntry.days.includes(day)) exEntry.days.push(day)
          } else {
            volume[muscleGroup].exercises.push({
              name: exercise.title,
              sets,
              days: [day],
              isPrimary
            })
          }
        })
      })
      })
    })

    return Object.values(volume).map((muscleVol) => ({
      ...muscleVol,
      weeklySetCount: Math.floor(muscleVol.weeklySetCount * cycleFrequency),
      dailyBreakdown: Object.fromEntries(
        Object.entries(muscleVol.dailyBreakdown).map(([day, sets]) => [day, sets * cycleFrequency])
      ),
      exercises: muscleVol.exercises.map(exercise => ({
        ...exercise,
        sets: Math.round(exercise.sets * cycleFrequency)
      }))
    }))
  }
}
